import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from 'app/core/services/config.service';
import { RequestService } from 'app/core/services/request.service';
import { Validators, FormBuilder } from '@angular/forms';
import { AmberApi } from 'app/core/models/api-helpers';

@Injectable()
export class CourseService {

    getOtherLanguages(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.configuration.otherLanguages.api;
        return this.requestService.get(url, { showKeys: true, sortByValue: true });
    }

    extractDictionaryFromConfigurationList(dto: any) {
        let result;
        if (Array.isArray(dto)) {
            result = [];
            dto.forEach(el => {
                if (el.hasOwnProperty('id')) {
                    result.push({
                        key: el['id'],
                        value: el['configValue']
                    })
                }
            });
        } else {
            result = dto;
        }
        return result;
    };

    getPopularLanguages(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.configuration.popularLanguages.api;
        return this.requestService.get(url, { showKeys: true, sortByValue: true });
    }

    createSectionFormGroup(type: string, title: string, description: string, duration: number) {
        return this.fb.group({
            type: this.fb.group({ configKey: [type, Validators.required] }),
            title: [title || null, Validators.required],
            description: [description || null],
            duration: [duration || null]
        })
    }

    convertMinutes(n: number) {
        const num = n;
        const hours = (num / 60);
        const rhours = Math.floor(hours);
        const minutes = (hours - rhours) * 60;
        const rminutes = Math.round(minutes);
        return `${rhours} h ${rminutes} min`;
    }

    constructor(private configService: ConfigService,
        private fb: FormBuilder,
        private requestService: RequestService) {
    }

    getCategories() {
        return [
            { key: 'category1', id: '1', value: 'Category 1' },
            { key: 'category2', id: '2', value: 'Category 2' },
            { key: 'category3', id: '3', value: 'Category 3' },
        ]
    }

    getDeliveryFormats(): Observable<any[]> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').agora.dictionary.deliveryFormat.api;
        return this.requestService.get(url, { verbose: true });
    }


    editPreCourseAgenda(details: any): Observable<AmberApi> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.precourse.updateDetails.api + '/' + 'agenda';
        return this.requestService.post(url, details);
    }


    getSubtopics(categoryId: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').agora.customTopics.listAvailableSubtopics.api + '/' + categoryId;
        return this.requestService.get(url);
    }

    getGroupDict(categoryId: string, domain: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').productSeller.dictionary.productGroupByCategoryId.api + '/' + categoryId;
        return this.requestService.get(url);
    }

    getTypeDict(): Observable<AmberApi> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.configuration.dictionaryByType.api +
            '?showKeys=true&@configType=internalRepositoryType&$configValue=1&and=1';
        return this.requestService.get(url);
    }

    getLevelDict(): Observable<AmberApi> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.configuration.dictionaryByType.api +
            '?@configType=courseLevel&$configValue=1&and=1';
        return this.requestService.get(url);
    }

    getCoursesDictByTypeListRoot(formatType: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.course.listRoot.api +
            '/' + formatType;
        return this.requestService.get(url);
    }

    updateAttribute(data: any, attribute: string): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.course.editAttribute.api + '/' + attribute;
        return this.requestService.post(url, data);
    }

    formatData(details: any) {
        const obj = {
            domains: details.domains ? details.domains.map(_ => {
                return { id: _ }
            }) : [],
            categoryFamily: details.categoryFamily ? details.categoryFamily.map(_ => {
                return { id: _ }
            }) : []
        };
        const keywords = details.keywords ? details.keywords : null;
        const subTopics = details.subTopics ? details.subTopics.map(el => {
            return { 'id': el }
        }) : [];
        const brands = details.brands ? details.brands.map(el => {
            return { 'id': el }
        }) : [];
        const category = details.category ? { 'id': details.category } : null;
        const type = details.type ? { 'id': details.type } : null;
        const group = details.group ? { 'id': details.group } : null;
        const level = details.level ? { 'id': details.level } : null;
        const segment = details.segment ? { 'id': details.segment } : null;
        details['subTopics'] = subTopics;
        details['brands'] = brands;
        details['category'] = category;
        details['keywords'] = keywords;
        details['type'] = type;
        details['group'] = group;
        details['level'] = level;
        details['segment'] = segment;
        let result = Object.assign({}, details);
        result = Object.assign(result, obj);
        return result;
    };

    keywordsSource = (request: any, response: any) => {
        const url = this.configService.get('server').url +
            this.configService.get('endpoints').productSeller.keywords.dictionary.api + '?filter=' + request['term'];

        return this.requestService.get(url)
            .map(_ => _['data'])
            .map(_ => {
                let result = [];
                _.forEach(element => {
                    result.push(element.key);
                });

                return result;
            })
            .subscribe(
                res => response(res)
            )
    };

    getFilters(): any {
        return {
            title: 'Course Types',
            key: 'format.configKey',
            show: true,
            state: '',
            options: [
                { checked: true, key: 'selfPacedCourse', required: true, value: 'Self-paced Course', frequency: 0 },
                { checked: true, key: 'instructorLedCourse', required: true, value: 'Instructor-led Course', frequency: 0 },
                // { checked: true, key: 'Course Event', required: true, value: 'Course Event', frequency: 0 },
            ]
        }
    }
    createAgendaWithCsv(payload: any) : Observable<any>{
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.precourse.updateAgendaWithCsv.api
        return this.requestService.post(url, payload);
      }
}



// WEBPACK FOOTER //
// ./src/app/solar/courses/course.service.ts